/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/**
 * @fileoverview Hook Actions Layer optimizado para manejo completo de Fast Answers en ChatComponentMC.
 * Proporciona todas las acciones de negocio para enviar, copiar y procesar respuestas rápidas, incluyendo manejo de mensajes múltiples y audio.
 * Cumple con arquitectura optimizada, separación de capas y performance avanzada.
 *
 * @module useFastAnswerActions
 * @category Hook - Actions Layer
 * @since 1.0.0
 * @version 2.1.0
 *
 * @description
 * **Arquitectura Optimizada (v2.1.0):**
 * - ✅ **Performance**: useCallback, useMemo y separación de lógica de negocio
 * - ✅ **Mantenibilidad**: Funciones extraídas y reutilizables
 * - ✅ **Error Handling**: Validaciones robustas y manejo de errores
 * - ✅ **Memory Management**: Optimización de dependencias y cleanup
 *
 * @typedef {Object} FastAnswerAttributes
 * @property {string} [file_type] - Tipo de archivo ('audio', 'file', 'image', etc.)
 * @property {string} [kind] - Tipo de respuesta ('text', 'text_image', etc.)
 * @property {string} [file_url] - URL del archivo
 * @property {string} [image_url] - URL de la imagen
 * @property {string} [file_name] - Nombre del archivo
 * @property {string} [answer] - Texto de la respuesta
 * @property {string|number} [id] - ID de la respuesta
 * @property {Object} [additional_fast_answers] - Respuestas adicionales
 *
 * @typedef {Object} ConversationContext
 * @property {string} platform - Plataforma activa
 * @property {string|number} customerId - ID del cliente (customers.id)
 * @property {string|number} [id] - ID del hilo en inbox; para comentarios FB/IG es customer_fb_posts.id (usar en outbound)
 * @property {boolean} [isComment] - Si es comentario
 *
 * @typedef {Object} UseFastAnswerActionsParams
 * @property {Object} textareaRef - Ref del textarea
 * @property {ConversationContext} conversation - Contexto de la conversación
 *
 * @typedef {Object} UseFastAnswerActionsReturn
 * @property {function} onSelectAnswer - Acción principal para seleccionar/copy/enviar respuesta
 * @property {function} copyFastAnswerToInput - Copia la respuesta al input
 * @property {function} sendFastAnswer - Envía la respuesta rápida
 *
 * @example
 * const { onSelectAnswer, copyFastAnswerToInput, sendFastAnswer } = useFastAnswerActions({ textareaRef, conversation });
 *
 * @see {@link useCompleteMessageActions} Hook de acciones de mensajes
 * @see {@link ChatComponentMC} Componente de chat principal
 */
import { useCallback, useEffect, useMemo } from 'react';
import useCompleteMessageActions from './useCompleteMessageActions';
import { sharingInformationService } from '../../../../services/sharingInformationService';
import COMMENT_THREAD_PLATFORMS from '../constants/commentThreadPlatforms';
import { isTikTokPlatform } from "../constants/platformMessageRestrictions";
import { isInboxMsChat } from '../../../../services/inbox/inboxMsChatPlatform';

const shouldAttachQuickAnswerReply = (platform, replyMsg) => (
  Boolean(replyMsg?.id) && !COMMENT_THREAD_PLATFORMS.has(platform)
);

/**
 * Matches legacy Comments#sendMultipleAnswers: one optimistic UUID per text slot and per file slot.
 * A single additional_fast_answer with both body and attachment must yield two local messages / identifiers.
 */
const expandCommentThreadAnswerSlots = (answersList) => {
  if (!Array.isArray(answersList) || answersList.length <= 1) return answersList;

  const [mainAttrs, ...additionalAttrs] = answersList;
  const expandedAdditional = additionalAttrs.flatMap((attrs) => {
    if (!attrs) return [];
    const hasText = attrs.answer != null && String(attrs.answer).trim() !== '';
    const fileUrl = attrs.file_url ?? attrs.image_url;
    const hasFile = Boolean(fileUrl && attrs.file_type);
    if (hasText && hasFile) {
      return [
        {
          ...attrs,
          file_url: undefined,
          image_url: undefined,
          file_type: undefined,
          file_name: undefined
        },
        { ...attrs, answer: undefined }
      ];
    }
    return [attrs];
  });

  return [mainAttrs, ...expandedAdditional];
};

/**
 * Customer id que se usa tanto para la clave del mensaje local en Redux como para el
 * payload del API (en MS esta rama no se usa, ver sendFastAnswer).
 *
 * Debe coincidir con conversationsReducer.selectedConversationId para pasar la validación
 * de MessageUpdateService (customer_id === conversationId):
 *  - Chat MS (multinumero WhatsApp, messenger, instagram): selectedConversationId es
 *    conversation.id (el hilo), distinto de customerId -> usamos conversation.id.
 *  - FB/IG comment threads: el API usa customer_fb_posts.id (conversation.id).
 *  - Resto (Rails legacy): conversation.id == customerId, así que conversation.customerId
 *    es equivalente y válido para ambos.
 */
const quickAnswersOutboundCustomerId = (conversation) => {
  if (!conversation) return undefined;
  if (COMMENT_THREAD_PLATFORMS.has(conversation.platform)) {
    return conversation.id;
  }
  if (isInboxMsChat(conversation.platform)) {
    return conversation.id;
  }
  return conversation.customerId;
};

const getMessageType = (attributes) => attributes.file_type ?? (attributes.kind === 'text_image' ? 'image' : attributes.kind ?? 'text');
const hasNonEmptyText = (value) => value != null && String(value).trim() !== '';

const buildMessageData = (attributes, conversation) => {
  const type = getMessageType(attributes);

  return {
    file_name: attributes.file_name ?? undefined,
    type,
    message: type === 'text' ? attributes.answer ?? undefined : undefined,
    caption: type !== 'text' ? attributes.answer ?? undefined : undefined,
    url: attributes.file_url ?? attributes.image_url ?? undefined,
    platform: conversation.platform,
    customer_id: quickAnswersOutboundCustomerId(conversation),
    isComment: !!conversation.isComment
  };
};

/**
 * WhatsApp `send_aditional_fast_answers` indexes `audio_message_identifiers` by additional row index
 * (same as `message_identifiers`). Pad with empty strings so FormData preserves positions.
 *
 * @param {string[]} audioCaptionIdsOnePerAdditional
 * @returns {string[]|undefined}
 */
const buildAudioMessageIdentifiersParam = (audioCaptionIdsOnePerAdditional) => {
  if (!audioCaptionIdsOnePerAdditional?.length) return undefined;
  const hasCaption = audioCaptionIdsOnePerAdditional.some((id) => id && String(id).trim() !== '');
  if (!hasCaption) return undefined;
  return audioCaptionIdsOnePerAdditional;
};

const buildFastAnswerPayload = (attributes, identifiers, audioCaptionIdsOnePerAdditional) => ({
  template: false,
  message_identifier: identifiers[0],
  template_id: attributes.id,
  type: attributes.file_type === 'file' ? 'document' : (attributes.file_type ?? 'text'),
  content_type: attributes.file_type === 'file' ? 'application/pdf' : (attributes.file_type ?? undefined),
  url: attributes.file_url ?? attributes.image_url ?? undefined,
  file_name: attributes.file_name ?? undefined,
  caption: attributes.image_url ? attributes.answer ?? undefined : undefined,
  message: !attributes.image_url ? attributes.answer ?? undefined : undefined,
  message_identifiers: identifiers.length > 1 ? identifiers.slice(1) : undefined,
  audio_message_identifiers: buildAudioMessageIdentifiersParam(audioCaptionIdsOnePerAdditional)
});

/**
 * Hook Actions Layer para manejo completo de Fast Answers en ChatComponentMC.
 *
 * Implementa el patrón de **Actions Layer** con separación estricta de responsabilidades,
 * enfocándose en operaciones de negocio para respuestas rápidas (envío, copia, procesamiento múltiple).
 *
 * @function useFastAnswerActions
 * @category Hook - Actions Layer
 *
 * @param {UseFastAnswerActionsParams} params - Parámetros del hook
 * @returns {UseFastAnswerActionsReturn} Acciones disponibles para respuestas rápidas
 *
 * @since 1.0.0
 * @version 2.1.0 - Documentación JSDoc completa y optimización de performance
 */
function useFastAnswerActions({
  textareaRef,
  conversation,
  setSelectedFastAnswer,
  selectedFastAnswer,
  replyMessage,
  clearReplyMessage
}) {
  const {
    addMessage,
    sendMessageOnly,
    buildLocalMessagePayload,
    buildLocalFileMessagePayload,
    sendMultipleFastAnswers,
    splitMessagePayloads
  } = useCompleteMessageActions();

  const processRegularMessage = useCallback(async (messageData) => {
    const localMessage = messageData.type === 'text'
      ? buildLocalMessagePayload(messageData, messageData.isComment)
      : buildLocalFileMessagePayload(messageData, messageData.customer_id, conversation.platform);

    await addMessage(localMessage);
    return localMessage;
  }, [
    addMessage,
    buildLocalFileMessagePayload,
    buildLocalMessagePayload,
    conversation
  ]);

  const principalReplyToId = useMemo(() => {
    if (!shouldAttachQuickAnswerReply(conversation?.platform, replyMessage)) return undefined;
    return replyMessage.id ?? replyMessage.identifier;
  }, [conversation?.platform, replyMessage]);

  const processAnswersList = useCallback(async (answersList) => {
    const identifiers = [];
    const additionalCount = Math.max((answersList?.length || 0) - 1, 0);
    const audioCaptionIdsOnePerAdditional = Array.from({ length: additionalCount }, () => '');
    let mainAudioFileIdentifier;

    if (!Array.isArray(answersList) || answersList.length === 0) {
      return { identifiers, audioCaptionIdsOnePerAdditional, mainAudioFileIdentifier };
    }

    const isMeta = ['messenger', 'instagram', 'facebook'].includes(conversation.platform);

    // Pre-filter: for TikTok, remove file/audio without text, convert file/audio+text to text only
    const filteredList = isTikTokPlatform(conversation.platform)
      ? answersList.filter((attrs) => {
        if (!attrs) return true;
        const type = getMessageType(attrs);
        if (['file', 'audio', 'video'].includes(type)) {
          return attrs.answer != null && String(attrs.answer).trim() !== '';
        }
        return true;
      }).map((attrs) => {
        if (!attrs) return attrs;
        const type = getMessageType(attrs);
        if (['file', 'audio', 'video'].includes(type)) {
          return {
            ...attrs,
            file_type: undefined,
            file_url: undefined,
            image_url: undefined,
            file_name: undefined
          };
        }
        return attrs;
      })
      : answersList;

    for (const [index, ansAttributes] of filteredList.entries()) {
      try {
        if (ansAttributes) {
          const messageData = buildMessageData(ansAttributes, conversation);
          if (index === 0 && principalReplyToId) {
            messageData.reply_to = principalReplyToId;
          }
          const isMedia = ['image', 'video', 'file', 'document'].includes(messageData.type);
          const shouldSplitMedia = isMeta && isMedia && messageData.caption;
          const shouldSplitAudio = messageData.type === 'audio' && hasNonEmptyText(messageData.caption || messageData.message);

          if (shouldSplitAudio || shouldSplitMedia) {
            const { identifiers: splitIds } = await splitMessagePayloads(messageData);
            if (messageData.type === 'audio') {
              const textId = splitIds[0];
              const fileId = splitIds[1];
              if (index === 0) {
                identifiers.push(textId);
                mainAudioFileIdentifier = fileId;
              } else {
                const additionalIdx = index - 1;
                identifiers.push(fileId);
                audioCaptionIdsOnePerAdditional[additionalIdx] = textId;
              }
            } else {
              splitIds.forEach((id) => identifiers.push(id));
            }
          } else {
            const localMessage = await processRegularMessage(messageData);
            identifiers.push(localMessage.identifier);
          }
        }
      } catch (error) {
        console.error(`processAnswersList: Error en índice ${index}:`, error);
      }
    }

    return { identifiers, audioCaptionIdsOnePerAdditional, mainAudioFileIdentifier };
  }, [
    splitMessagePayloads,
    processRegularMessage,
    conversation,
    principalReplyToId
  ]);

  /**
   * Elimina la respuesta rápida seleccionada y limpia el input.
   */
  const onRemoveFastAnswer = useCallback(() => {
    setSelectedFastAnswer(null);
    // Si hay textarea, limpiar y restaurar foco
    if (textareaRef.current) {
      // eslint-disable-next-line no-param-reassign
      textareaRef.current.value = '';
      textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    });
  }, [textareaRef, setSelectedFastAnswer]);

  const sendFastAnswer = useCallback(async (answer) => {
    // Validación temprana de datos requeridos
    if (!answer?.attributes || !conversation) return;

    try {
      const { attributes } = answer;
      let answersList = [attributes, ...(attributes.additional_fast_answers?.data?.map((a) => a.attributes) || [])];
      if (COMMENT_THREAD_PLATFORMS.has(conversation.platform)) {
        answersList = expandCommentThreadAnswerSlots(answersList);
      }

      const {
        identifiers,
        audioCaptionIdsOnePerAdditional,
        mainAudioFileIdentifier
      } = await processAnswersList(answersList);
      const messageFastAnswers = buildFastAnswerPayload(
        attributes,
        identifiers,
        audioCaptionIdsOnePerAdditional
      );
      const outboundCustomerId = quickAnswersOutboundCustomerId(conversation);
      messageFastAnswers.customer_id = isInboxMsChat(conversation.platform)
        ? conversation.customerId
        : outboundCustomerId;
      messageFastAnswers.chat_id = conversation.id ? String(conversation.id) : String(outboundCustomerId);
      messageFastAnswers.platform = conversation.platform;

      if (shouldAttachQuickAnswerReply(conversation.platform, replyMessage)) {
        messageFastAnswers.reply_to = replyMessage.id;
      }

      // Manejo especial solo para audio (Texto primero, luego Audio)
      const hasMainAudioCaption = hasNonEmptyText(attributes.answer);
      if (attributes.file_type === 'audio' && hasMainAudioCaption) {
        const textPayload = {
          template: false,
          customer_id: isInboxMsChat(conversation.platform)
            ? conversation.customerId
            : outboundCustomerId,
          chat_id: conversation.id ? String(conversation.id) : String(outboundCustomerId),
          type: 'text',
          message: attributes.answer || '',
          platform: conversation.platform,
          message_identifier: identifiers[0]
        };
        if (shouldAttachQuickAnswerReply(conversation.platform, replyMessage)) {
          textPayload.reply_to = replyMessage.id;
        }
        await sendMessageOnly(textPayload);

        // A reply applies only to the principal (intro text) bubble, not the follow-up audio.
        delete messageFastAnswers.reply_to;

        // Ajustar payload para enviar el audio después
        messageFastAnswers.message_identifier = mainAudioFileIdentifier;
        messageFastAnswers.caption = undefined;
        messageFastAnswers.message = undefined;
      } else if (attributes.file_type === 'audio') {
        // Si el caption está vacío, no se envía ni se pinta fake de texto.
        messageFastAnswers.caption = undefined;
        messageFastAnswers.message = undefined;
      }

      // Envío final (para audio, imágenes Meta o casos normales)
      // La división de imágenes en Meta ahora la maneja el backend en el orden correcto (Imagen -> Texto)
      await sendMultipleFastAnswers(messageFastAnswers);
      // onRemoveFastAnswer();
    } catch (error) {
      console.error('Error en sendFastAnswer:', error);
    }
  }, [
    conversation,
    replyMessage,
    // onRemoveFastAnswer,
    processAnswersList,
    sendMessageOnly,
    sendMultipleFastAnswers
  ]);

  const copyFastAnswerToInput = useCallback((answer) => {
    // Validación robusta de dependencias
    if (!answer?.attributes?.answer || !textareaRef?.current) return;

    try {
      const message = answer.attributes.answer.trim();
      if (!message) return;

      // Actualizar estado seleccionado para UI
      setSelectedFastAnswer(answer);

      // Actualizar valor en react-hook-form
      // Establecer el mensaje en el textarea directamente
      if (textareaRef.current) {
        // eslint-disable-next-line no-param-reassign
        textareaRef.current.value = message;
        textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      }

      // Optimizar manipulación del DOM con requestAnimationFrame
      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.focus();
          const textLength = message.length;
          textarea.setSelectionRange(textLength, textLength);

          // Trigger de evento para actualizar altura si es necesario
          textarea.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
    } catch (error) {
      console.error('Error en copyFastAnswerToInput:', error);
    }
  }, [textareaRef, setSelectedFastAnswer]);

  const onSelectAnswer = useCallback((answer, actionType) => {
    if (!answer || !actionType) {
      console.warn('onSelectAnswer: Parámetros requeridos faltantes');
      return;
    }

    switch (actionType) {
      case 'send':
        sendFastAnswer(answer);
        onRemoveFastAnswer();
        clearReplyMessage?.();
        break;
      case 'copy':
        copyFastAnswerToInput(answer);
        break;
      default:
        console.warn(`onSelectAnswer: Acción no reconocida: ${actionType}`);
        break;
    }
  }, [sendFastAnswer, copyFastAnswerToInput, onRemoveFastAnswer, clearReplyMessage]);

  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();
    const myObservable = subscription$.subscribe((data) => {
      if (data.onSelectAnswer) {
        onSelectAnswer(data.answer, data.actionType);
      }
    });
    return () => {
      myObservable.unsubscribe();
    };
  }, [onSelectAnswer]);
  /**
   * Handler para envío de fast answer desde el input principal del chat
   * @param {string} message - Mensaje a enviar
   * @returns {boolean} - true si había fast answer y se envió, false si no
   */
  const handleSendFastAnswerFromInput = useCallback((message) => {
    if (selectedFastAnswer) {
      const copyAnswer = { ...selectedFastAnswer };
      copyAnswer.attributes.answer = message;
      sendFastAnswer(copyAnswer);
      onRemoveFastAnswer();
      clearReplyMessage?.();
      return true;
    }
    return false;
  }, [selectedFastAnswer, sendFastAnswer, onRemoveFastAnswer, clearReplyMessage]);

  // Retorno memoizado para evitar recreación innecesaria del objeto
  return useMemo(() => ({
    onSelectAnswer,
    copyFastAnswerToInput,
    sendFastAnswer,
    onRemoveFastAnswer,
    handleSendFastAnswerFromInput
  }), [
    onSelectAnswer,
    copyFastAnswerToInput,
    sendFastAnswer,
    onRemoveFastAnswer,
    handleSendFastAnswerFromInput
  ]);
}

export default useFastAnswerActions;
